/*
 * 昭然AI外挂补丁 - 长期记忆库管理 API
 *
 * 修复记录：
 * 1. 路径修正：后端注册在 /v1/memory/longterm/xxx，Nginx 代理 /v1/ 到后端
 * 2. 实例化：直接使用 axiosInstance，与其他 API 保持一致，不再依赖未定义的 CozeRequest
 * 3. 响应处理：后端返回 {code, msg, data}，axios 拦截器返回 response.data，需手动提取 data 字段
 */

import { axiosInstance } from './axios';

export interface LongTermMemoryBase {
  id: number | string;
  space_id: number | string;
  creator_id?: number | string;
  owner_name?: string;
  name: string;
  description?: string;
  icon_uri?: string;
  isolate_by_user?: boolean;
  fact_count?: number;
  created_at_ms?: number;
  updated_at_ms?: number;
}

export interface LongTermMemoryFact {
  id: number | string;
  memory_base_id: number | string;
  user_id?: string;
  fact_text: string;
  fact_type?: string;
  source_query?: string;
  importance?: number;
  created_at_ms?: number;
  updated_at_ms?: number;
}

export interface CreateLongTermMemoryReq {
  space_id: number | string;
  name: string;
  owner_name?: string;
  description?: string;
  icon_uri?: string;
  isolate_by_user?: boolean;
}

export interface UpdateLongTermMemoryReq {
  id: number | string;
  name?: string;
  owner_name?: string;
  description?: string;
  icon_uri?: string;
  isolate_by_user?: boolean;
}

export interface BindLongTermMemoryReq {
  bot_id: number | string;
  memory_id: number | string;
}

export interface UnbindLongTermMemoryReq {
  bot_id: number | string;
  memory_id: number | string;
}

export interface ListBotLongTermMemoryReq {
  bot_id: number | string;
}

export interface ListFactsReq {
  memory_id: number | string;
  offset?: number;
  limit?: number;
}

export interface ListFactsResp {
  facts: LongTermMemoryFact[];
  total: number;
}

export interface AddFactReq {
  memory_id: number | string;
  fact_text: string;
  fact_type?: string;
  source_query?: string;
  importance?: number;
}

export interface DeleteFactReq {
  fact_id: number | string;
}

export interface ClearFactsReq {
  memory_id: number | string;
}

interface ApiResp<T> {
  code: number;
  msg: string;
  data: T;
}

const BASE = '/v1/memory/longterm';

const extractData = <T>(resp: ApiResp<T>): T => {
  if (resp && resp.code === 0) {
    return resp.data;
  }
  throw new Error(resp?.msg || '请求失败');
};

class LongTermMemoryApi {
  list = async (params: { space_id: number | string }): Promise<LongTermMemoryBase[]> => {
    const resp = await axiosInstance.post<ApiResp<LongTermMemoryBase[]>>(`${BASE}/list`, params);
    return extractData(resp) || [];
  };

  get = async (params: { id: number | string; memory_id?: number | string }): Promise<LongTermMemoryBase> => {
    // backend expects {id} for get_by_id
    const req = { id: String(params.id || params.memory_id || '') };
    const resp = await axiosInstance.post<ApiResp<LongTermMemoryBase>>(`${BASE}/get_by_id`, req);
    return extractData(resp);
  };

  create = async (params: CreateLongTermMemoryReq): Promise<LongTermMemoryBase> => {
    const resp = await axiosInstance.post<ApiResp<LongTermMemoryBase>>(`${BASE}/create`, params);
    return extractData(resp);
  };

  update = async (params: UpdateLongTermMemoryReq): Promise<boolean> => {
    const resp = await axiosInstance.post<ApiResp<boolean>>(`${BASE}/update`, params);
    return extractData(resp);
  };

  delete = async (params: { id: number | string }): Promise<boolean> => {
    const resp = await axiosInstance.post<ApiResp<boolean>>(`${BASE}/delete`, params);
    return extractData(resp);
  };

  bindToBot = async (params: BindLongTermMemoryReq): Promise<LongTermMemoryBase> => {
    // backend path is /bind_to_bot
    const resp = await axiosInstance.post<ApiResp<LongTermMemoryBase>>(`${BASE}/bind_to_bot`, params);
    return extractData(resp);
  };

  unbindFromBot = async (params: UnbindLongTermMemoryReq): Promise<boolean> => {
    // backend path is /unbind_to_bot
    const resp = await axiosInstance.post<ApiResp<boolean>>(`${BASE}/unbind_to_bot`, params);
    return extractData(resp);
  };

  listBotMemory = async (params: ListBotLongTermMemoryReq): Promise<LongTermMemoryBase[]> => {
    const resp = await axiosInstance.post<ApiResp<LongTermMemoryBase[]>>(`${BASE}/list_bot_memory`, params);
    return extractData(resp) || [];
  };

  listFacts = async (params: ListFactsReq): Promise<ListFactsResp> => {
    const resp = await axiosInstance.post<ApiResp<ListFactsResp>>(`${BASE}/list_facts`, params);
    return extractData(resp) || { facts: [], total: 0 };
  };

  addFact = async (params: AddFactReq): Promise<LongTermMemoryFact> => {
    const resp = await axiosInstance.post<ApiResp<LongTermMemoryFact>>(`${BASE}/add_fact`, params);
    return extractData(resp);
  };

  deleteFact = async (params: DeleteFactReq): Promise<boolean> => {
    const resp = await axiosInstance.post<ApiResp<boolean>>(`${BASE}/delete_fact`, params);
    return extractData(resp);
  };

  clearFacts = async (params: ClearFactsReq): Promise<{ deleted: number }> => {
    const resp = await axiosInstance.post<ApiResp<{ deleted: number }>>(`${BASE}/clear_facts`, params);
    return extractData(resp) || { deleted: 0 };
  };
}

export const longTermMemoryApi = new LongTermMemoryApi();

export type { LongTermMemoryBase as LongTermMemoryBaseType, LongTermMemoryFact as LongTermMemoryFactType };
